<template>
  <TemplateLidBase>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Header -->
      <header
        class="flex flex-col sm:flex-row sm:items-end sm:w-4/5 lg:w-2/3 gap-y-4 gap-x-8 xl:gap-x-12 mb-8 sm:mb-12 md:mb-16 xl:mb-24 2xl:mb-32 mt-10 md:mt-12 xl:mt-16 2xl:mt-20 mx-4 lg:mx-6 xl:mx-12"
      >
        <h1 class="article-overview-title font-black leading-none text-4xl sm:text-5xl md:text-6xl">{{ page.title }}</h1>
        <p v-if="page.subtitle" class="article-overview-subtitle sm:text-lg md:text-xl lg:text-xl xl:text-3xl 2xl:text-4xl leading-tight">{{ page.subtitle }}</p>
      </header>

      <!-- ARTICLES -->
      <section v-if="articles.length > 0" class="grid grid-cols-1 sm:grid-cols-12 gap-y-16 gap-x-8 xl:gap-x-12 xl:gap-y-24 mx-4 lg:mx-6 xl:mx-12 mb-24 xl:mb-40">
        <MoleculeLidHighlightArticle
          :date="articles[0].date"
          :summary="articles[0].introduction"
          :url="articles[0].url"
          :title="articles[0].title"
          :image="articles[0].largeImage"
          class="col-span-full"
        />
        <MoleculeCarouselLidCarouselSlideStory
          v-for="article in articles.slice(1)"
          :key="article.id"
          :date="article.date"
          :url="article.url"
          :title="article.title"
          :image="article.mediumImage"
          class="col-span-full sm:col-span-6 lg:col-span-4"
        />
      </section>

      <!-- PAGINATION -->
      <section class="container relative mb-12 mx-auto">
        <OrganismPagination
          :count="totalHits"
          :limit="hitsPerPage"
          :page="pageNumber"
          :theme="{ color: 'text-white', background: 'bg-app-lid--blue-smalt', backgroundHover: 'bg-app-lid--blue-prussian' }"
          @click="({ page }) => setPageNumber(page)"
        />
      </section>
    </main>
  </TemplateLidBase>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useArticleOverviewStore } from '~/store/article-overview'
import { routerQueryIsEqual } from '@/composables/setup-dynamic-url'

interface Props {
  banner?: object
  page: object
}

withDefaults(defineProps<Props>(), {
  banner: undefined,
})

const route = useRoute()
const router = useRouter()
const articleOverviewStore = useArticleOverviewStore()

const articles = computed(() => articleOverviewStore.articles)
const hitsPerPage = computed(() => articleOverviewStore.hitsPerPage)
const pageNumber = computed(() => articleOverviewStore.pageNumber)
const totalHits = computed(() => articleOverviewStore.totalHits)
const urlParameters = computed(() => articleOverviewStore.urlParameters)

const setPageNumber = articleOverviewStore.setPageNumber

watch(urlParameters, (query) => {
  if (!routerQueryIsEqual(route.query, query)) {
    router.push({ query })
  }
})
</script>

<style scoped lang="scss">
.article-overview-title {
  @screen lg {
    font-size: 72px;
  }

  @screen xl {
    font-size: 96px;
  }

  @screen 2xl {
    font-size: 120px;
  }
}

.article-overview-subtitle {
  @screen sm {
    margin-bottom: 4px;
  }

  @screen md {
    margin-bottom: 8px;
  }

  @screen lg {
    margin-bottom: 10px;
  }

  @screen 2xl {
    margin-bottom: 12px;
  }
}
</style>
